import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: d });
export const pct = (n) => `${Number(n || 0).toFixed(3)}%`;

const Kpi = ({ label, value, tone = 'text-foreground', testid, sub }) => (
  <Card className="shadow-none" data-testid={testid}>
    <CardContent className="p-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-xl font-mono font-semibold mt-1 ${tone}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

export const KpiCards = ({ totals: t, targets }) => {
  const qOk = t.quarantine_pct <= targets.target_quarantine_pct;
  const rOk = t.rejection_pct <= targets.target_rejection_pct;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
      <Kpi label="Produced" value={fmt(t.produced, 2)} testid="kpi-produced" sub="OK + quarantined" />
      <Kpi label="Quarantined" value={fmt(t.quarantined, 2)} tone="text-amber-600" testid="kpi-quarantined" />
      <Kpi label="Rejected" value={fmt(t.rejected, 2)} tone={rOk ? 'text-foreground' : 'text-red-600'} testid="kpi-rejected" />
      <Kpi label="Reworked / Re-accepted" value={`${fmt(t.reworked, 2)} / ${fmt(t.reaccepted, 2)}`} testid="kpi-rework-reaccept" />
      <Kpi label="Pending MRB" value={fmt(t.pending, 2)} tone="text-orange-600" testid="kpi-pending" />
      <Kpi label={`Quarantine % (target ${targets.target_quarantine_pct}%)`} value={pct(t.quarantine_pct)}
        tone={qOk ? 'text-emerald-600' : 'text-red-600'} testid="kpi-quarantine-pct" sub={`${fmt(t.quarantine_ppm)} PPM`} />
      <Kpi label={`Rejection % (target ${targets.target_rejection_pct}%)`} value={pct(t.rejection_pct)}
        tone={rOk ? 'text-emerald-600' : 'text-red-600'} testid="kpi-rejection-pct" />
      <Kpi label="Rejection PPM" value={fmt(t.rejection_ppm)} tone={rOk ? 'text-emerald-600' : 'text-red-600'} testid="kpi-rejection-ppm" />
    </div>
  );
};

export const CustomerTiles = ({ c }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ppm-customer-tiles">
    <Kpi label="Dispatched (OK)" value={fmt(c.dispatched, 2)} testid="kpi-customer-dispatched" />
    <Kpi label="Customer complaints" value={`${c.complaints}`} sub={`${c.open_complaints} not closed`} testid="kpi-customer-complaints" />
    <Kpi label="Customer rejected qty" value={fmt(c.rejected, 2)} tone={c.rejected ? 'text-red-600' : 'text-foreground'} testid="kpi-customer-rejected" />
    <Kpi label="Customer PPM" value={fmt(c.ppm)} tone={c.ppm ? 'text-red-600' : 'text-emerald-600'} sub="complaint qty vs dispatched" testid="kpi-customer-ppm" />
  </div>
);

export const TrendChart = ({ data, targets, onMonthClick }) => (
  <Card className="shadow-none lg:col-span-2" data-testid="ppm-trend-chart">
    <CardHeader className="pb-1">
      <CardTitle className="text-sm">Monthly quarantine / rejection PPM and % <span className="text-xs font-normal text-muted-foreground">— click a month to drill down</span></CardTitle>
    </CardHeader>
    <CardContent className="h-72">
      {data.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No production in this period</p> : (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} onClick={(e) => e?.activeLabel && onMonthClick(e.activeLabel)} style={{ cursor: 'pointer' }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="pct" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
            <YAxis yAxisId="ppm" orientation="right" tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => (n.includes('%') ? `${Number(v).toFixed(3)}%` : fmt(v))} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine yAxisId="pct" y={targets.target_rejection_pct} stroke="#dc2626" strokeDasharray="5 4"
              label={{ value: `Rej target ${targets.target_rejection_pct}%`, fontSize: 10, position: 'insideTopLeft' }} />
            <Bar yAxisId="ppm" dataKey="quarantine_ppm" name="Quarantine PPM" fill="#fbbf24" />
            <Bar yAxisId="ppm" dataKey="rejection_ppm" name="Rejection PPM" fill="#f87171" />
            <Line yAxisId="pct" dataKey="rejection_pct" name="Rejection %" stroke="#b91c1c" strokeWidth={2} />
            <Line yAxisId="pct" dataKey="quarantine_pct" name="Quarantine %" stroke="#d97706" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

export const ReasonList = ({ rows }) => (
  <Card className="shadow-none" data-testid="ppm-by-reason">
    <CardHeader className="pb-1"><CardTitle className="text-sm">By reason (quarantined qty)</CardTitle></CardHeader>
    <CardContent className="space-y-3">
      {rows.length === 0 && <p className="text-xs text-muted-foreground">No quarantine in this period</p>}
      {rows.map((r) => (
        <div key={r.reason}>
          <div className="flex justify-between text-xs gap-2"><span className="truncate uppercase">{r.reason}</span><span className="font-mono whitespace-nowrap">{fmt(r.quantity, 2)} · {r.share_pct}%</span></div>
          <div className="h-1.5 bg-muted rounded mt-1"><div className="h-1.5 bg-red-500 rounded" style={{ width: `${r.share_pct}%` }} /></div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const NUM_COLS = [
  ['produced', 'Produced', 2], ['quarantined', 'Quarantined', 2], ['rejected', 'Rejected', 2],
  ['reworked', 'Reworked', 2], ['quarantine_ppm', 'Q PPM', 0], ['rejection_pct', 'Rej %', null], ['rejection_ppm', 'Rej PPM', 0], ['share_pct', 'Share', null],
];

export const BreakdownTable = ({ title, rows, labelOf, keyOf, onPick, testid, targetPct, rowAction }) => (
  <Card className="shadow-none" data-testid={testid}>
    <CardHeader className="pb-1"><CardTitle className="text-sm">{title} ({rows.length})</CardTitle></CardHeader>
    <CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-muted-foreground"><tr>
          <th className="text-left px-3 py-2 font-medium">Name</th>
          {NUM_COLS.map(([k, l]) => <th key={k} className="text-right px-2 py-2 font-medium">{l}</th>)}
          {rowAction && <th className="px-2 py-2" />}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="text-center py-4 text-muted-foreground">No data</td></tr>}
          {rows.map((r) => (
            <tr key={keyOf(r)} className="border-t hover:bg-muted/40 cursor-pointer" onClick={() => onPick(r)} data-testid={`${testid}-row-${keyOf(r)}`}>
              <td className="px-3 py-2 text-blue-700 underline-offset-2 hover:underline">{labelOf(r)}</td>
              {NUM_COLS.map(([k, , d]) => (
                <td key={k} className={`text-right px-2 py-2 font-mono ${k === 'rejection_pct' && r[k] > targetPct ? 'text-red-600 font-semibold' : ''} ${k === 'rejected' && r[k] > 0 ? 'text-red-600' : ''}`}>
                  {k === 'rejection_pct' ? pct(r[k]) : k === 'share_pct' ? `${r[k]}%` : fmt(r[k], d)}
                </td>
              ))}
              {rowAction && <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>{rowAction(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

const DISP_TONE = { reject: 'bg-red-100 text-red-700', rework: 'bg-blue-100 text-blue-700', reaccept: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700' };

export const DetailTable = ({ rows, onOpenCase }) => (
  <Card className="shadow-none" data-testid="ppm-detail-table">
    <CardHeader className="pb-1"><CardTitle className="text-sm">MRB detail ({rows.length}) <span className="text-xs font-normal text-muted-foreground">— click an NCR to open the MRB case</span></CardTitle></CardHeader>
    <CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-muted-foreground"><tr>
          {['Date', 'NCR', 'WO', 'Item', 'Program', 'Customer', 'Qty', 'Disposition', 'Rejected', 'Reason', 'Status', 'By'].map((h) => <th key={h} className="text-left px-2 py-2 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={12} className="text-center py-4 text-muted-foreground">No MRB cases</td></tr>}
          {rows.map((r) => (
            <tr key={r.case_id} className="border-t" data-testid={`ppm-detail-row-${r.case_id}`}>
              <td className="px-2 py-2 font-mono">{r.date}</td>
              <td className="px-2 py-2"><button className="text-blue-700 hover:underline font-mono" onClick={() => onOpenCase(r.case_id)} data-testid={`ppm-open-case-${r.case_id}`}>{r.ncr_number || 'Open'}</button></td>
              <td className="px-2 py-2 font-mono">{r.wo_number}</td>
              <td className="px-2 py-2"><span className="font-mono">{r.item_unique_id}</span> <span className="text-muted-foreground">{r.item_name}</span></td>
              <td className="px-2 py-2">{r.program_name}</td>
              <td className="px-2 py-2">{r.customer}</td>
              <td className="px-2 py-2 font-mono">{fmt(r.quarantined, 2)}</td>
              <td className="px-2 py-2"><Badge variant="outline" className={`border-0 text-[10px] ${DISP_TONE[r.disposition] || ''}`}>{r.disposition}</Badge></td>
              <td className="px-2 py-2 font-mono text-red-600">{r.rejected ? fmt(r.rejected, 2) : '-'}</td>
              <td className="px-2 py-2 max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
              <td className="px-2 py-2">{r.status}</td>
              <td className="px-2 py-2 text-muted-foreground">{r.by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);
